/*
  Keeps the card's image, price, stock row and add-to-cart target in sync with
  the selected finish, fits the finish strip to its own measured width behind
  a "+N" tile, and surfaces a rejected add inline on the card.

  Submitting the form itself is deliberately NOT handled here: the card's own
  <form is="product-form"> and <button is="loader-button"> (see the liquid)
  already borrow Focal's native add-to-cart pipeline -- disabling the button
  synchronously before the fetch, posting to the cart, re-rendering the mini
  cart section and dispatching variant:added / cart:refresh /
  cart-notification:show. Duplicating any of that here would race the native
  handler rather than help it. This element only listens for the one signal
  Focal's pipeline does not scope per card: cart-notification:show tells the
  whole page a request finished, and this uses it to paint (or clear) this
  card's own out-of-flow status region without growing the card.

  the snippet renders this script tag once per card in a grid, so the same
  file executes multiple times per page; wrapped in an IIFE so the class
  declaration gets a fresh scope each time instead of colliding with the
  previous one, and guarded so the second execution does not redefine the
  custom element
*/
(function () {
  if (customElements.get('kludi-card')) return;

  class KludiCard extends HTMLElement {
    connectedCallback() {
      // the form and its cta exist on every card, with or without a finish axis, so
      // this wiring must not sit behind the finish-input guard below
      this.form = this.querySelector('.kludi-card__form');
      this.error = this.querySelector('.kludi-card__error');
      if (this.form) {
        this.addEventListener('cart-notification:show', (event) => this.onNotification(event));
      }

      this.inputs = Array.from(this.querySelectorAll('.kludi-card__finish-input'));
      if (this.inputs.length === 0) return;

      this.image = this.querySelector('.kludi-card__image');
      this.price = this.querySelector('.kludi-card__price');
      this.stock = this.querySelector('.kludi-card__stock');
      this.variantField = this.querySelector('.kludi-card__variant-id');

      this.addEventListener('change', (event) => {
        if (event.target.classList.contains('kludi-card__finish-input')) this.select(event.target);
      });

      // every finish value is in the DOM (see the liquid); measuring which ones
      // actually fit the strip is this element's own job, because liquid cannot
      // see the rendered width and a fixed cap sized for the desktop card was
      // wrong on anything narrower
      this.finishStrip = this.querySelector('.kludi-card__finishes');
      this.finishLabels = Array.from(this.querySelectorAll('.kludi-card__finish'));
      this.finishMore = this.querySelector('.kludi-card__finish-more');
      if (this.finishStrip && this.finishLabels.length) {
        this.finishResizeObserver = new ResizeObserver(() => this.updateFinishOverflow());
        this.finishResizeObserver.observe(this.finishStrip);
      }
    }

    disconnectedCallback() {
      if (this.finishResizeObserver) this.finishResizeObserver.disconnect();
    }

    /*
      decides which finish swatches fit the strip's own measured width and hides
      the rest behind the "+N" tile, recomputing on every resize rather than
      once at load, so an intermediate width is never left with a stale,
      desktop-derived set

      the fit is measured against --kludi-finish-item-rem, not the item's actual
      rendered width: on a surface that can hover, the item's rest state is the
      narrow dot, but it grows to the wider thumb on hover, and that custom
      property already resolves to the wider figure there (see the css). sizing
      on the narrow state would let a hovered row overflow
    */
    updateFinishOverflow() {
      const total = this.finishLabels.length;
      if (!total) return;

      // read as literal pixel numbers, not multiplied against the root font-size:
      // unlike Dawn, Focal does not rebase the root to a 10px-per-rem convention
      // (its own root font-size is a real, unscaled design value -- 14/15px, see
      // assets/theme.css), so this card's own css (kludi-card-product.css) holds
      // every one of its own lengths in px rather than rem, and these two custom
      // properties are the one place that px number also has to reach this script
      const stripStyle = getComputedStyle(this.finishStrip);
      const itemPx = parseFloat(stripStyle.getPropertyValue('--kludi-finish-item-px')) || 38;
      const gapPx = parseFloat(stripStyle.getPropertyValue('--kludi-finish-gap-px')) || 8;
      const available = this.finishStrip.clientWidth;

      const widthFor = (count) => (count > 0 ? count * itemPx + (count - 1) * gapPx : 0);

      // start optimistic (everything fits, no tile needed), then fall back to
      // fewer real swatches plus one tile slot until the arithmetic actually fits
      let visible = total;
      let showMore = false;
      if (widthFor(total) > available) {
        showMore = true;
        visible = total - 1;
        while (visible > 0 && widthFor(visible + 1) > available) visible--;
      }
      // a slot always exists for at least one real swatch, even on a card too
      // narrow for the arithmetic above to satisfy cleanly, because the checked
      // finish (see below) has to live somewhere
      visible = Math.max(visible, 1);

      const hiddenCount = total - visible;
      const checkedIndex = this.finishLabels.findIndex(
        (label) => label.querySelector('.kludi-card__finish-input')?.checked,
      );

      const visibleIndexes = new Set();
      for (let i = 0; i < visible; i++) visibleIndexes.add(i);
      // the checked finish drives the card's price, image and stock line, so it can
      // never end up among the hidden ones -- swap the last visible slot for it
      if (checkedIndex >= 0 && !visibleIndexes.has(checkedIndex)) {
        const lastVisible = [...visibleIndexes].pop();
        visibleIndexes.delete(lastVisible);
        visibleIndexes.add(checkedIndex);
      }

      // the `hidden` attribute, not a class toggling opacity or width: a hidden
      // finish must be unreachable by pointer, keyboard and screen reader alike,
      // not merely invisible, and it must stop contributing to the fieldset's
      // own layout width entirely
      this.finishLabels.forEach((label, i) => {
        label.hidden = !visibleIndexes.has(i);
      });

      if (!this.finishMore) return;
      this.finishMore.hidden = !(showMore && hiddenCount > 0);
      if (!this.finishMore.hidden) {
        const text = `+${hiddenCount}`;
        const dot = this.finishMore.querySelector('.kludi-card__finish-more-dot');
        const thumb = this.finishMore.querySelector('.kludi-card__finish-more-thumb');
        if (dot) dot.textContent = text;
        if (thumb) {
          thumb.textContent = text;
          // the "other" label was rendered with a placeholder count (see the liquid);
          // substituting the real number keeps every word of the sentence translated,
          // rather than assembling any of it as literal text here
          thumb.setAttribute(
            'aria-label',
            hiddenCount === 1 ? thumb.dataset.labelOne : thumb.dataset.labelOther.replace(/\d+/, String(hiddenCount)),
          );
        }
      }
    }

    select(input) {
      const available = input.dataset.available === 'true';

      if (this.image && input.dataset.image) {
        this.image.src = input.dataset.image;
        this.image.removeAttribute('srcset');
      }

      if (this.price && input.dataset.price) {
        this.price.textContent = input.dataset.price;
      }

      if (this.stock) {
        this.stock.classList.toggle('kludi-card__stock--out', !available);
        this.stock.textContent = available ? this.dataset.inStock : this.dataset.soldOut;
      }

      if (this.variantField) this.variantField.value = input.dataset.variantId;

      const cta = this.querySelector('.kludi-card__cta');
      if (cta) {
        /*
          only the disabled state is set here: the button carries its add-to-cart
          and sold-out labels as three sibling spans and the stylesheet picks the
          right one, so nothing rewrites the button's text. writing textContent
          here would replace those spans with a single text node and permanently
          break the mobile/desktop label swap
        */
        cta.disabled = !available;
      }
    }

    /*
      Focal's own ProductForm (assets/theme.js) dispatches this event, bubbling,
      on the form itself after every add-to-cart attempt, success or failure,
      with the API's own description on failure. Listening here rather than
      overriding the form's submit keeps this card on Focal's tested pipeline
      (see the file header) and only adds the one thing that pipeline does not
      already provide: a status region scoped to this card, out of flow so a
      rejected add cannot grow it and misalign the grid row it sits in
    */
    onNotification(event) {
      if (!this.error || event.target !== this.form) return;

      if (event.detail?.status === 'error') {
        this.error.textContent = event.detail.error || '';
        this.error.hidden = false;
      } else {
        this.error.textContent = '';
        this.error.hidden = true;
      }
    }
  }

  customElements.define('kludi-card', KludiCard);
})();
